import React, { useState } from 'react';
import { LayoutDashboard, FileText, Terminal, Receipt, FileSpreadsheet, Users, Truck, Wallet, ArrowRightLeft, Store, ShoppingCart, Megaphone, BarChart, Link as LinkIcon, Webhook, Settings, HelpCircle, Search, Bell, Plus, ChevronDown, Info, Calendar, Clock, CreditCard, Edit2, AlertCircle, Code, Menu, X, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ApplicationStatusPanel } from '../components/ApplicationStatusPanel';
export function MerchantDashboard() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isStatusPanelOpen, setIsStatusPanelOpen] = useState(false);
  const sidebarGroups = [{
    title: '',
    items: [{
      icon: LayoutDashboard,
      label: 'Dashboard',
      active: true
    }]
  }, {
    title: 'MERCHANTS & PARTNERS',
    items: [{
      icon: FileText,
      label: 'Payment requests'
    }, {
      icon: Terminal,
      label: 'Terminals',
      hasSub: true
    }, {
      icon: Receipt,
      label: 'Invoices'
    }, {
      icon: FileSpreadsheet,
      label: 'Bills'
    }]
  }, {
    title: 'RELATIONSHIPS',
    items: [{
      icon: Users,
      label: 'Customers'
    }, {
      icon: Truck,
      label: 'Suppliers'
    }]
  }, {
    title: 'PAYMENTS',
    items: [{
      icon: Wallet,
      label: 'Balance',
      hasSub: true
    }, {
      icon: ArrowRightLeft,
      label: 'Transactions',
      hasSub: true
    }]
  }, {
    title: 'E-COMMERCE',
    items: [{
      icon: Store,
      label: 'Shops'
    }, {
      icon: ShoppingCart,
      label: 'Orders'
    }, {
      icon: Megaphone,
      label: 'Campaigns'
    }]
  }, {
    title: 'REPORTS & ANALYTICS',
    items: [{
      icon: BarChart,
      label: 'Reporting',
      hasSub: true
    }]
  }, {
    title: 'DEVELOPERS',
    items: [{
      icon: LinkIcon,
      label: 'Connect'
    }, {
      icon: Webhook,
      label: 'Webhooks',
      hasSub: true
    }]
  }, {
    title: 'SYSTEM',
    items: [{
      icon: Settings,
      label: 'Settings'
    }, {
      icon: HelpCircle,
      label: 'Tutorials'
    }]
  }];
  const EmptyChartPlaceholder = () => <div className="mt-4 flex-1 bg-gray-50 border border-dashed border-gray-200 rounded flex items-center justify-center p-4 text-center min-h-[120px]" data-id="element-1035">
      <span className="text-xs text-gray-400 max-w-[200px]" data-id="element-1036">
        Data will populate once your account is live and processing
        transactions.
      </span>
    </div>;
  const MetricCard = ({
    title,
    value,
    subValue
  }: {
    title: string;
    value: string;
    subValue?: string;
  }) => <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col shadow-sm" data-id="element-1037">
      <div className="flex items-center gap-1.5 mb-2" data-id="element-1038">
        <span className="text-sm font-medium text-gray-800" data-id="element-1039">{title}</span>
        <Info className="w-3.5 h-3.5 text-gray-400" data-id="element-1040" />
      </div>
      <div className="flex items-baseline gap-2 mb-1" data-id="element-1041">
        <span className="text-2xl font-bold text-gray-900" data-id="element-1042">{value}</span>
        {subValue && <span className="text-sm font-medium text-gray-500" data-id="element-1043">{subValue}</span>}
      </div>
      <div className="text-xs text-gray-400 mb-2" data-id="element-1044">Updated just now</div>
      <EmptyChartPlaceholder data-id="element-1045" />
    </div>;
  const ListCard = ({
    title,
    children
  }: {
    title: string;
    children: React.ReactNode;
  }) => <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col shadow-sm" data-id="element-1046">
      <div className="flex items-center gap-1.5 mb-4" data-id="element-1047">
        <span className="text-sm font-medium text-gray-800" data-id="element-1048">{title}</span>
        <Info className="w-3.5 h-3.5 text-gray-400" data-id="element-1049" />
      </div>
      <div className="flex-1 flex flex-col" data-id="element-1050">{children}</div>
    </div>;
  return <div className="min-h-screen bg-gray-50 flex font-sans text-gray-900" data-id="element-1051">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setIsMobileMenuOpen(false)} data-id="element-1052" />}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#111827] text-gray-400 flex flex-col transition-transform duration-300 md:relative md:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`} data-id="element-1053">
        {/* Logo Area */}
        <div className="h-16 flex items-center px-4 border-b border-gray-800 flex-shrink-0" data-id="element-1054">
          <div className="flex items-center gap-3" data-id="element-1055">
            <div className="w-8 h-8 bg-[#FFCC00] rounded flex items-center justify-center" data-id="element-1056">
              <span className="text-black font-bold text-lg" data-id="element-1057">M</span>
            </div>
            <span className="font-bold text-white text-lg tracking-tight" data-id="element-1058">
              Maybank
            </span>
          </div>
          <button className="ml-auto md:hidden text-gray-400 hover:text-white" onClick={() => setIsMobileMenuOpen(false)} data-id="element-1059">
            <X className="w-5 h-5" data-id="element-1060" />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 overflow-y-auto py-4 custom-scrollbar" data-id="element-1061">
          {sidebarGroups.map((group, idx) => <div key={idx} className="mb-6" data-id="element-1062">
              {group.title && <div className="px-4 mb-2 text-[10px] font-bold tracking-wider text-gray-500 uppercase" data-id="element-1063">
                  {group.title}
                </div>}
              <ul className="space-y-0.5" data-id="element-1064">
                {group.items.map((item, itemIdx) => {
              const Icon = item.icon;
              return <li key={itemIdx} data-id="element-1065">
                      <a href="#" className={`flex items-center justify-between px-4 py-2 text-sm transition-colors ${item.active ? 'text-[#FFCC00] bg-gray-800/50 border-l-2 border-[#FFCC00]' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/30 border-l-2 border-transparent'}`} data-id="element-1066">
                        <div className="flex items-center gap-3" data-id="element-1067">
                          <Icon className={`w-4 h-4 ${item.active ? 'text-[#FFCC00]' : 'text-gray-500'}`} data-id="element-1068" />
                          <span className={item.active ? 'font-medium' : ''} data-id="element-1069">
                            {item.label}
                          </span>
                        </div>
                        {item.hasSub && <ChevronDown className="w-3.5 h-3.5 text-gray-600" data-id="element-1070" />}
                      </a>
                    </li>;
            })}
              </ul>
            </div>)}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden" data-id="element-1071">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6 flex-shrink-0" data-id="element-1072">
          <div className="flex items-center flex-1 gap-4" data-id="element-1073">
            <button className="md:hidden text-gray-500 hover:text-gray-700" onClick={() => setIsMobileMenuOpen(true)} data-id="element-1074">
              <Menu className="w-5 h-5" data-id="element-1075" />
            </button>

            {/* Search Bar */}
            <div className="relative w-full max-w-2xl hidden sm:block" data-id="element-1076">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1077">
                <Search className="h-4 w-4 text-gray-400" data-id="element-1078" />
              </div>
              <input type="text" placeholder="Search transactions, customers, invoices..." className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:bg-white focus:ring-1 focus:ring-[#FFCC00] focus:border-[#FFCC00] sm:text-sm transition-colors" data-id="element-1079" />
            </div>
          </div>

          {/* Right Icons */}
          <div className="flex items-center gap-3 sm:gap-5 ml-4" data-id="element-1080">
            <button className="text-gray-400 hover:text-gray-600" data-id="element-1081">
              <HelpCircle className="w-5 h-5" data-id="element-1082" />
            </button>
            <button className="text-gray-400 hover:text-gray-600 relative" data-id="element-1083">
              <Bell className="w-5 h-5" data-id="element-1084" />
              <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full border-2 border-white" data-id="element-1085"></span>
            </button>
            <button className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-100 transition-colors" data-id="element-1086">
              <Plus className="w-4 h-4" data-id="element-1087" />
            </button>
            <div className="h-6 w-px bg-gray-200 mx-1 hidden sm:block" data-id="element-1088"></div>
            <button className="flex items-center gap-2 hover:bg-gray-50 p-1 rounded-md transition-colors" data-id="element-1089">
              <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden border border-gray-300" data-id="element-1090">
                <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Merchant" alt="Avatar" className="w-full h-full object-cover" data-id="element-1091" />
              </div>
              <span className="text-sm font-medium text-gray-700 hidden sm:block" data-id="element-1092">
                Merchant
              </span>
              <ChevronDown className="w-4 h-4 text-gray-500 hidden sm:block" data-id="element-1093" />
            </button>
          </div>
        </header>

        {/* Scrollable Dashboard Content */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-8" data-id="element-1094">
          <div className="max-w-[1600px] mx-auto" data-id="element-1095">
            {/* Current Snapshot Section */}
            <h1 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1096">
              Current snapshot
            </h1>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8" data-id="element-1097">
              {/* Available Balance Card */}
              <div className="bg-white border border-gray-200 rounded-lg flex flex-col shadow-sm" data-id="element-1098">
                <div className="p-5 flex-1" data-id="element-1099">
                  <div className="flex justify-between items-center mb-6" data-id="element-1100">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-800" data-id="element-1101">
                      <Wallet className="w-4 h-4 text-green-600" data-id="element-1102" /> Available
                      balance
                    </div>
                    <button className="text-sm text-blue-600 hover:underline font-medium flex items-center" data-id="element-1103">
                      View all <ChevronRight className="w-4 h-4 ml-0.5" data-id="element-1104" />
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 mb-1" data-id="element-1105">
                    Updated just now
                  </div>
                  <div className="text-3xl font-bold text-gray-900" data-id="element-1106">
                    MYR 0.00
                  </div>
                </div>
                <div className="bg-orange-50/50 border-t border-orange-100 p-4 flex justify-between items-center rounded-b-lg" data-id="element-1107">
                  <div className="flex items-center gap-2 text-sm text-orange-800 font-medium" data-id="element-1108">
                    <Calendar className="w-4 h-4 text-orange-600" data-id="element-1109" /> Upcoming
                    payout · MYR 0.00 · Pending
                  </div>
                  <button className="text-sm text-orange-600 hover:underline font-medium flex items-center" data-id="element-1110">
                    View payout details{' '}
                    <ChevronRight className="w-4 h-4 ml-0.5" data-id="element-1111" />
                  </button>
                </div>
              </div>

              {/* Attention Needed / Application Status */}
              <div className="bg-white border border-gray-200 rounded-lg flex flex-col shadow-sm" data-id="element-1112">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50/50 rounded-t-lg" data-id="element-1113">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-900" data-id="element-1114">
                    <AlertCircle className="w-4 h-4 text-red-500" data-id="element-1115" /> Attention
                    needed
                  </div>
                  <span className="bg-red-50 text-red-700 text-xs px-2.5 py-0.5 rounded-full font-medium border border-red-100" data-id="element-1116">
                    1 urgent
                  </span>
                </div>
                <div className="p-5 flex-1 flex flex-col justify-center gap-4" data-id="element-1117">
                  {/* Status Alert */}
                  <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-md" data-id="element-1118">
                    <Info className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-1119" />
                    <div className="flex-1" data-id="element-1120">
                      <div className="flex items-center justify-between" data-id="element-1121">
                        <p className="text-sm text-yellow-900 font-semibold" data-id="element-1122">
                          Application under review
                        </p>
                        <button onClick={() => setIsStatusPanelOpen(true)} className="text-xs font-bold text-yellow-900 hover:text-yellow-700 underline decoration-yellow-400 underline-offset-2" data-id="element-1123">
                          Check status
                        </button>
                      </div>
                      <p className="text-xs text-yellow-800 mt-1" data-id="element-1124">
                        Live payment processing is disabled until you are
                        approved.
                      </p>
                    </div>
                  </div>

                  {/* Next Steps Alert */}
                  <div className="flex items-start gap-3 p-4 bg-gray-50 border border-gray-200 rounded-md" data-id="element-1125">
                    <Code className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" data-id="element-1126" />
                    <div className="flex-1 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-1127">
                      <div data-id="element-1128">
                        <p className="text-sm text-gray-900 font-semibold" data-id="element-1129">
                          Next Steps: Integration
                        </p>
                        <p className="text-xs text-gray-600 mt-1" data-id="element-1130">
                          Start integrating Maybank payments using test mode.
                        </p>
                      </div>
                      <Button size="sm" className="whitespace-nowrap text-xs h-8 px-3" data-id="element-1131">
                        Get test API keys
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Overview Section */}
            <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1132">Overview</h2>

            {/* Filters Row */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6" data-id="element-1133">
              <div className="flex flex-wrap items-center gap-2" data-id="element-1134">
                <button className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm" data-id="element-1135">
                  <Calendar className="w-4 h-4 text-gray-500" data-id="element-1136" /> Apr 28, 2026 -
                  May 5, 2026
                </button>
                <button className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm" data-id="element-1137">
                  <Clock className="w-4 h-4 text-gray-500" data-id="element-1138" /> Daily{' '}
                  <ChevronDown className="w-4 h-4 text-gray-500" data-id="element-1139" />
                </button>
                <button className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-sm font-medium text-blue-700 hover:bg-blue-100 shadow-sm" data-id="element-1140">
                  <ArrowRightLeft className="w-4 h-4 text-blue-500" data-id="element-1141" /> Compare |
                  Previous period{' '}
                  <ChevronDown className="w-4 h-4 text-blue-500" data-id="element-1142" />
                </button>
                <button className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm" data-id="element-1143">
                  <CreditCard className="w-4 h-4 text-gray-500" data-id="element-1144" /> All payment
                  methods <ChevronDown className="w-4 h-4 text-gray-500" data-id="element-1145" />
                </button>
              </div>
              <div className="flex items-center gap-2" data-id="element-1146">
                <button className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm" data-id="element-1147">
                  <Plus className="w-4 h-4 text-gray-500" data-id="element-1148" /> Add
                </button>
                <button className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm" data-id="element-1149">
                  <Edit2 className="w-4 h-4 text-gray-500" data-id="element-1150" /> Edit
                </button>
              </div>
            </div>

            {/* 3-Column Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pb-12" data-id="element-1151">
              {/* Row 1 */}
              <MetricCard title="Gross volume" value="MYR 0.00" subValue="MYR 0.00 previous period" data-id="element-1152" />
              <MetricCard title="Net volume" value="MYR 0.00" subValue="MYR 0.00 previous period" data-id="element-1153" />
              <MetricCard title="Successful payments" value="0" subValue="0 previous period" data-id="element-1154" />

              {/* Row 2 */}
              <MetricCard title="Payment success rate" value="0.0%" subValue="0.0% previous period" data-id="element-1155" />
              <MetricCard title="Refunds volume" value="MYR 0.00" subValue="MYR 0.00 previous period" data-id="element-1156" />
              <MetricCard title="Refund rate" value="0.0%" subValue="0.0% previous period" data-id="element-1157" />

              {/* Row 3 */}
              <ListCard title="Total payments" data-id="element-1158">
                <div className="space-y-3 mb-4" data-id="element-1159">
                  <div className="flex justify-between text-sm" data-id="element-1160">
                    <span className="text-gray-600" data-id="element-1161">Succeeded</span>
                    <span className="font-medium" data-id="element-1162">0</span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1163">
                    <span className="text-gray-600" data-id="element-1164">Authorized</span>
                    <span className="font-medium" data-id="element-1165">0</span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1166">
                    <span className="text-gray-600" data-id="element-1167">Failed</span>
                    <span className="font-medium" data-id="element-1168">0</span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1169">
                    <span className="text-gray-600" data-id="element-1170">
                      Refunded & Partially refunded
                    </span>
                    <span className="font-medium" data-id="element-1171">0</span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1172">
                    <span className="text-gray-600" data-id="element-1173">Refund failed</span>
                    <span className="font-medium" data-id="element-1174">0</span>
                  </div>
                </div>
                <div className="mt-auto pt-4 border-t border-gray-100 flex justify-between items-center" data-id="element-1175">
                  <span className="text-xs text-gray-400" data-id="element-1176">
                    Updated just now
                  </span>
                  <button className="text-sm text-blue-600 hover:underline" data-id="element-1177">
                    View more
                  </button>
                </div>
              </ListCard>
              <MetricCard title="Dispute count" value="0" subValue="0 previous period" data-id="element-1178" />
              <MetricCard title="Dispute activity" value="0.0%" subValue="0.0% previous period" data-id="element-1179" />

              {/* Row 4 */}
              <ListCard title="Disputed payments" data-id="element-1180">
                <EmptyChartPlaceholder data-id="element-1181" />
                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center" data-id="element-1182">
                  <span className="text-xs text-gray-400" data-id="element-1183">
                    Updated just now
                  </span>
                  <button className="text-sm text-blue-600 hover:underline" data-id="element-1184">
                    View more
                  </button>
                </div>
              </ListCard>
              <ListCard title="Failed payments" data-id="element-1185">
                <EmptyChartPlaceholder data-id="element-1186" />
                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center" data-id="element-1187">
                  <span className="text-xs text-gray-400" data-id="element-1188">
                    Updated just now
                  </span>
                  <button className="text-sm text-blue-600 hover:underline" data-id="element-1189">
                    View more
                  </button>
                </div>
              </ListCard>
              <ListCard title="Top payment method categories" data-id="element-1190">
                <EmptyChartPlaceholder data-id="element-1191" />
                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center" data-id="element-1192">
                  <span className="text-xs text-gray-400" data-id="element-1193">
                    Updated just now
                  </span>
                  <button className="text-sm text-blue-600 hover:underline" data-id="element-1194">
                    View more
                  </button>
                </div>
              </ListCard>

              {/* Row 5 */}
              <MetricCard title="New customers" value="0" subValue="0 previous period" data-id="element-1195" />
              <MetricCard title="Spend per customer" value="MYR 0.00" subValue="MYR 0.00 previous period" data-id="element-1196" />
              <ListCard title="Top customers by spend" data-id="element-1197">
                <EmptyChartPlaceholder data-id="element-1198" />
                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center" data-id="element-1199">
                  <span className="text-xs text-gray-400" data-id="element-1200">
                    Updated just now
                  </span>
                  <button className="text-sm text-blue-600 hover:underline" data-id="element-1201">
                    View more
                  </button>
                </div>
              </ListCard>

              {/* Row 6 */}
              <ListCard title="Top countries by spend" data-id="element-1202">
                <EmptyChartPlaceholder data-id="element-1203" />
                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center" data-id="element-1204">
                  <span className="text-xs text-gray-400" data-id="element-1205">
                    Updated just now
                  </span>
                  <button className="text-sm text-blue-600 hover:underline" data-id="element-1206">
                    View more
                  </button>
                </div>
              </ListCard>
              <MetricCard title="Blocked payments" value="0" subValue="0 previous period" data-id="element-1207" />
              <MetricCard title="Blocked payments volume" value="MYR 0.00" subValue="MYR 0.00 previous period" data-id="element-1208" />
            </div>
          </div>
        </div>
      </main>

      {/* Application Status Slide-over Panel */}
      <ApplicationStatusPanel isOpen={isStatusPanelOpen} onClose={() => setIsStatusPanelOpen(false)} data-id="element-1209" />
    </div>;
}